import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, Star, Trophy, Settings, BookOpen, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import MobileMenu from "@/components/MobileMenu";
import { useAuth } from "@/contexts/AuthContext";

// Shared hamburger menu used across the account-y pages (Lobby /
// Profile / Leaderboard / Settings / Season). Hides the link to the
// page the user is already on so the menu doesn't offer the no-op
// "go where you already are" route.
//
// `current` is one of: "lobby" | "profile" | "leaderboard" | "settings" | "season".
// Pass null/undefined to render every item — useful for pages that
// don't sit in this nav group.
export default function AccountNavMenu({ current }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = [
    {
      key: "profile",
      to: "/profile",
      icon: User,
      label: t("lobby.nav.profile"),
      ariaLabel: t("lobby.label.profile"),
      testid: "profile-btn",
    },
    {
      key: "season",
      to: "/season-pass",
      icon: Star,
      label: t("lobby.nav.season"),
      ariaLabel: t("lobby.label.seasonPass"),
      testid: "season-pass-btn",
    },
    {
      key: "leaderboard",
      to: "/leaderboard",
      icon: Trophy,
      label: t("lobby.nav.leaderboard"),
      ariaLabel: t("lobby.label.leaderboard"),
      testid: "leaderboard-btn",
    },
    {
      key: "rules",
      to: "/rules",
      icon: BookOpen,
      label: t("lobby.nav.rules"),
      ariaLabel: t("lobby.nav.rules"),
      testid: "rules-btn",
    },
    {
      key: "settings",
      to: "/settings",
      icon: Settings,
      label: t("lobby.nav.settings"),
      ariaLabel: t("lobby.label.settings"),
      testid: "settings-btn",
    },
  ];

  return (
    <MobileMenu>
      {items
        .filter((it) => it.key !== current)
        .map(({ key, to, icon: Icon, label, ariaLabel, testid }) => (
          <Button
            key={key}
            variant="ghost"
            size="sm"
            onClick={() => navigate(to)}
            data-testid={testid}
            className="justify-start rounded-full sm:justify-center"
            aria-label={ariaLabel}
          >
            <Icon className="w-4 h-4 mr-2 sm:mr-1" aria-hidden="true" />
            <span>{label}</span>
          </Button>
        ))}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleLogout}
        data-testid="logout-btn"
        className="rounded-full text-[#FF5A5F] justify-start sm:justify-center"
        aria-label={t("lobby.label.logOut")}
      >
        <LogOut className="w-4 h-4 mr-2 sm:mr-0" aria-hidden="true" />
        <span className="sm:hidden">{t("lobby.label.logOut")}</span>
      </Button>
    </MobileMenu>
  );
}
